import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { GROUPS, HEALTH_CHOICES, HEALTH_LABELS, PHASE_LABELS, QUICK_VIEWS, STATUS_LABELS, customerErrorMessage, formatDay } from "../lib/customers";
import { HealthBadge, PhaseBadge, StatusBadge } from "../components/customers/CustomerBits";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { AlertTriangle, ChevronRight, Loader2, RefreshCw, Search, SlidersHorizontal, Store, X } from "lucide-react";

const ALL = "__all__";
const PAGE_SIZE = 50;
const GROUP_PHASES = { deploying: ["to_start", "onboarding", "onboarding_done"], live: ["live"], closed: [] };
const GROUP_STATUSES = { deploying: ["pre_activation"], live: ["active", "suspended"], closed: [] };
const MANAGER_CHOICES = [{ id: "me", label: "Moi" }, { id: "none", label: "Sans responsable" }];

// Filters live in the address (so the tableau de bord, the search palette and a shared link land on the same view); paging,
// order and scope are the server's.
function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    group: GROUP_PHASES[params.get("group")] ? params.get("group") : "deploying",
    groupChosen: !!GROUP_PHASES[params.get("group")],
    phase: params.get("phase") || "",
    status: params.get("status") || "",
    manager: params.get("manager") || "",
    mine: params.get("mine") === "1",
    region: params.get("region") || "",
    search: params.get("q") || "",
    health: HEALTH_CHOICES.includes(params.get("health")) ? params.get("health") : "",
    review: ["overdue", "due"].includes(params.get("review")) ? params.get("review") : "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = key === "search" ? "q" : key;
        if (value === "" || value === false || value == null) next.delete(name);
        else next.set(name, value === true ? "1" : value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function PersonCell({ person }) {
  if (!person) return <span className="text-slate-400">—</span>;
  return <span className={person.active ? "" : "text-slate-500"}>{person.name}{person.active ? "" : " (désactivé)"}</span>;
}

function MobileRow({ item }) {
  return (
    <Link to={`/clients/${item.prospect_id}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
      data-testid={`customers-card-${item.prospect_id}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="font-medium text-slate-900 break-words">{item.pharmacy.name}</div>
        <div className="flex flex-wrap gap-1.5">
          <PhaseBadge phase={item.phase} /><StatusBadge status={item.status} closeCode={item.close_code} />
          {item.health?.state && <HealthBadge state={item.health.state} overridden={item.health.overridden} testid={`customers-card-health-${item.prospect_id}`} />}
        </div>
        {item.health?.fact && item.health.state !== "on_track" && <div className="text-xs text-slate-600">{item.health.fact}</div>}
        <div className="text-xs text-slate-500">
          {[item.pharmacy.region, item.account_manager ? `Responsable : ${item.account_manager.name}` : "Sans responsable",
            item.service_activated_at ? `En service depuis le ${formatDay(item.service_activated_at)}` : null].filter(Boolean).join(" · ")}
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

export default function Customers() {
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0, counts: {}, regions: [] });
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { group: filters.group, limit: PAGE_SIZE };
    for (const key of ["phase", "status", "manager", "region", "search", "health", "review"]) if (filters[key]) out[key] = filters[key];
    if (filters.mine) out.mine = true;
    return out;
  }, [filters.group, filters.phase, filters.status, filters.manager, filters.mine, filters.region, filters.search, filters.health, filters.review]); // eslint-disable-line react-hooks/exhaustive-deps

  // A response that arrives after a newer request is dropped.
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/customers", { params: query });
      if (mine !== seq.current) return;
      // A link without a group (tableau de bord, search palette: « À risque », « Revue dépassée ») opens the first group that holds
      // something rather than an empty « À démarrer » tab; the tabs show the counts, so nothing is hidden.
      const firstFull = GROUPS.find((g) => body.counts?.[g.id] > 0)?.id;
      if (!filters.groupChosen && body.items.length === 0 && firstFull && firstFull !== filters.group) {
        update({ group: firstFull });
        return;
      }
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(customerErrorMessage(e, "Impossible de charger les clients."));
      setPhase("error");
    }
  }, [query, filters.groupChosen, filters.group, update]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/customers", { params: { ...query, skip: data.items.length } });
      if (mine !== seq.current) return;
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.prospect_id === i.prospect_id))] }));
    } catch (e) {
      if (mine === seq.current) toast.error(customerErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const changeGroup = (group) => update({ group, phase: "", status: "" });
  const phases = GROUP_PHASES[filters.group];
  const statuses = GROUP_STATUSES[filters.group];
  const activeFilters = [filters.phase, filters.status, filters.manager, filters.region, filters.health].filter(Boolean).length;
  const filtered = activeFilters > 0 || filters.mine || !!filters.search || !!filters.review;
  const reset = () => { setSearchInput(""); update({ phase: "", status: "", manager: "", region: "", mine: false, search: "", health: "", review: "" }); };
  const quickActive = (view) => Object.entries(view.params).every(([k, v]) => filters[k] === v);
  const toggleQuick = (view) => update(Object.fromEntries(Object.entries(view.params).map(([k, v]) => [k, quickActive(view) ? "" : v])));

  const selects = (
    <>
      {phases.length > 1 && (
        <Select value={filters.phase || ALL} onValueChange={(v) => update({ phase: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full md:w-48" aria-label="Phase" data-testid="customers-phase-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les phases</SelectItem>
            {phases.map((p) => <SelectItem key={p} value={p}>{PHASE_LABELS[p]}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {statuses.length > 1 && (
        <Select value={filters.status || ALL} onValueChange={(v) => update({ status: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full md:w-44" aria-label="Statut" data-testid="customers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les statuts</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {filters.group !== "closed" && (
        <Select value={filters.health || ALL} onValueChange={(v) => update({ health: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full md:w-40" aria-label="Santé" data-testid="customers-health-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les santés</SelectItem>
            {HEALTH_CHOICES.map((h) => <SelectItem key={h} value={h}>{HEALTH_LABELS[h]}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Select value={filters.manager || ALL} onValueChange={(v) => update({ manager: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-48" aria-label="Responsable du compte" data-testid="customers-manager-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les responsables</SelectItem>
          {MANAGER_CHOICES.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {data.regions.length > 1 && (
        <Select value={filters.region || ALL} onValueChange={(v) => update({ region: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full md:w-40" aria-label="Région" data-testid="customers-region-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les régions</SelectItem>
            {data.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </>
  );

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Clients</h1>
        <p className="text-sm text-slate-500">Suivez les pharmacies gagnées, de leur mise en service à la vie du contrat.</p>
      </div>

      <div className="flex gap-1" role="tablist" aria-label="Groupe de dossiers" data-testid="customers-groups">
        {GROUPS.map((g) => (
          <button key={g.id} type="button" role="tab" aria-selected={filters.group === g.id} onClick={() => changeGroup(g.id)} data-testid={`customers-group-${g.id}`}
            className={`flex-auto sm:flex-none rounded-full px-2.5 sm:px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${filters.group === g.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <span className="sm:hidden">{g.short}</span><span className="hidden sm:inline">{g.label}</span>
            {data.counts[g.id] != null && <span className={`ml-1.5 tabular-nums ${filters.group === g.id ? "text-white/80" : "text-slate-400"}`}>{data.counts[g.id]}</span>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Rechercher une pharmacie…" className="pl-9 h-9"
            aria-label="Rechercher un client" data-testid="customers-search" />
        </div>
        <Button variant={filters.mine ? "default" : "outline"} size="sm" className="h-9 shrink-0" aria-pressed={filters.mine}
          onClick={() => update({ mine: !filters.mine })} data-testid="customers-mine-toggle">Mes clients</Button>
        {QUICK_VIEWS.map((view) => (
          <Button key={view.id} variant={quickActive(view) ? "default" : "outline"} size="sm" className="h-9 shrink-0" aria-pressed={quickActive(view)}
            onClick={() => toggleQuick(view)} data-testid={`customers-quick-${view.id}`}>{view.label}</Button>
        ))}
        <div className="hidden md:flex items-center gap-2">{selects}</div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="md:hidden h-9 gap-1.5 shrink-0" aria-label="Filtres" data-testid="customers-filters-toggle">
              <SlidersHorizontal size={14} />{activeFilters > 0 && <span className="text-xs font-semibold">{activeFilters}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" title="Filtres" className="w-72 space-y-3 md:hidden">{selects}</PopoverContent>
        </Popover>
        {filtered && (
          <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset} data-testid="customers-reset"><X size={14} className="mr-1" />Effacer</Button>
        )}
      </div>

      {phase === "loading" && (
        <div className="space-y-2" data-testid="customers-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      )}

      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="customers-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="customers-empty">
          <Store className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucun client ne correspond</h3>
              <p className="text-sm text-slate-500">Modifiez ou effacez les filtres pour élargir la liste.</p>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucun dossier dans ce groupe</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Un dossier client s'ouvre tout seul quand une pharmacie passe à « Gagné » dans le pipeline.</p>
            </>
          )}
        </Card>
      )}

      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="customers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pharmacie</TableHead><TableHead>Phase</TableHead><TableHead>Statut</TableHead><TableHead>Santé</TableHead>
                  <TableHead>Responsable du compte</TableHead>
                  <TableHead className="hidden lg:table-cell">Commercial</TableHead><TableHead>Mise en service</TableHead>
                  <TableHead className="hidden xl:table-cell">Prochaine revue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.prospect_id} className="cursor-pointer" onClick={() => navigate(`/clients/${i.prospect_id}`)} data-testid={`customers-row-${i.prospect_id}`}>
                    <TableCell>
                      <Link to={`/clients/${i.prospect_id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{i.pharmacy.name}</Link>
                      {(i.pharmacy.commune || i.pharmacy.region) && <div className="text-xs text-slate-500">{[i.pharmacy.commune, i.pharmacy.region].filter(Boolean).join(" · ")}</div>}
                    </TableCell>
                    <TableCell><PhaseBadge phase={i.phase} /></TableCell>
                    <TableCell><StatusBadge status={i.status} closeCode={i.close_code} /></TableCell>
                    <TableCell className="max-w-[16rem]">
                      <HealthBadge state={i.health?.state} overridden={i.health?.overridden} testid={`customers-health-${i.prospect_id}`} />
                      {i.health?.fact && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{i.health.fact}</div>}
                    </TableCell>
                    <TableCell className="text-sm"><PersonCell person={i.account_manager} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-sm"><PersonCell person={i.commercial} /></TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDay(i.service_activated_at) || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm tabular-nums">{formatDay(i.next_review_date) || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="customers-list-mobile">{data.items.map((i) => <MobileRow key={i.prospect_id} item={i} />)}</Card>

          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="customers-count">{data.items.length} sur {data.total} dossier{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="customers-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
